package diana.soleil.design.iraniancanadienbazzar.auth

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.common.Validators
import diana.soleil.design.iraniancanadienbazzar.core.data.UserProfileRepository
import diana.soleil.design.iraniancanadienbazzar.core.firebase.AuthService
import diana.soleil.design.iraniancanadienbazzar.core.firebase.AppleSignInController
import diana.soleil.design.iraniancanadienbazzar.core.firebase.GoogleSignInController
import diana.soleil.design.iraniancanadienbazzar.core.model.AuthUser
import diana.soleil.design.iraniancanadienbazzar.core.model.UserAccount
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.mapLatest
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch
import org.koin.core.module.dsl.viewModel
import org.koin.dsl.module

/** Transient UI state for the sign-in / sign-up form (the session itself lives in [AuthViewModel.session]). */
data class AuthUiState(
    val loading: Boolean = false,
    val error: AppError? = null,
    val resetEmailSent: Boolean = false,
    /** The email typed for a password reset was malformed — shown as a specific format error. */
    val invalidEmail: Boolean = false,
)

/** Lifecycle of the destructive account-deletion flow (confirmation sheet in the main scaffold). */
data class DeleteAccountUiState(
    val working: Boolean = false,
    /** Wrong password / stale session — the sheet shows the localized error and stays open. */
    val error: AppError? = null,
    /** Deletion finished; the scaffold navigates back to Welcome and resets this state. */
    val done: Boolean = false,
)

/**
 * Owns the app-wide auth session ([session]) and drives the sign-in/sign-up/reset flows over
 * [AuthService]. Lives in `:shared` (the composition root) because authentication gates navigation and
 * is cross-feature; per-user *data* still flows through `:core:data` repositories.
 *
 * The form is validated locally before hitting the network (empty fields short-circuit to
 * [AppError.InvalidCredentials]); on success the form state is cleared and [session] flips, which the
 * sign-in route observes to navigate onward.
 */
class AuthViewModel(
    private val auth: AuthService,
    private val users: UserProfileRepository,
    private val googleSignIn: GoogleSignInController,
    private val appleSignIn: AppleSignInController,
) : ViewModel() {

    /** True only when this platform has a wired Google flow — the sign-in screens gate the button on it. */
    val googleSignInAvailable: Boolean = googleSignIn.isAvailable

    /** True only on platforms with a wired Sign in with Apple flow (iOS) — gates the Apple button. */
    val appleSignInAvailable: Boolean = appleSignIn.isAvailable

    /** The signed-in user, or `null` when signed out. Seeded with the current snapshot to avoid a flash. */
    val session: StateFlow<AuthUser?> =
        auth.authState.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), auth.currentUser)

    /**
     * Whether Firebase has restored the persisted session yet — flips `true` on the first auth-state
     * emission (a real user OR a confirmed signed-out). The nav host gates its start destination on
     * this: `currentUser` is null until the SDK finishes restoring (async on web/JS), so deciding
     * Welcome-vs-Main from the snapshot alone bounced returning users to Welcome. `Eagerly` so the
     * first emission is captured at app start, before the splash gate subscribes.
     */
    val sessionResolved: StateFlow<Boolean> =
        auth.authState.map { true }.stateIn(viewModelScope, SharingStarted.Eagerly, false)

    /**
     * The signed-in account's role (`users/{uid}.role` — `admin`/`moderator`/`user`), or null for a
     * guest/anonymous session. Re-resolved on every session change. Drives UI gating only (which
     * console entry point Profile shows); Firestore rules are the real enforcement.
     */
    @OptIn(ExperimentalCoroutinesApi::class)
    val role: StateFlow<String?> = session
        .mapLatest { user ->
            if (user == null || user.isAnonymous) null
            else (users.getRole(user.uid) as? DataResult.Success)?.data
        }
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), null)

    /** Whether the signed-in account is a full admin (`role == 'admin'`). */
    val isAdmin: StateFlow<Boolean> = role
        .map { it == "admin" }
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), false)

    /**
     * Whether the account may work the moderation queue (admin OR moderator). Gates the console entry
     * point on Profile; the console itself scopes its sections by role.
     */
    val canModerate: StateFlow<Boolean> = role
        .map { UserAccount.canModerate(it) }
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), false)

    private val _ui = MutableStateFlow(AuthUiState())
    val ui: StateFlow<AuthUiState> = _ui.asStateFlow()

    fun signIn(email: String, password: String) = submitCredentials(email, password) {
        auth.signIn(email, password)
    }

    fun signUp(email: String, password: String) = submitCredentials(email, password) {
        auth.signUp(email, password)
    }

    /**
     * Registration from the dedicated Sign Up page: creates the account, then sets the display name
     * on the user's profile. The name update is best-effort — a failure there must not fail the
     * whole registration (the account exists and the session flips regardless).
     */
    fun signUp(name: String, email: String, password: String) = submitCredentials(email, password) {
        val result = auth.signUp(email, password)
        if (result is DataResult.Success && name.isNotBlank()) {
            runCatching { users.updateProfile(displayName = name.trim(), bio = "") }
        }
        result
    }

    /**
     * Google sign-in: asks the platform for a native ID token (Credential Manager sheet on Android),
     * then exchanges it for a Firebase session ([AuthService.signInWithGoogle], which links a guest's
     * data). A null token = the user dismissed the sheet → silently clear loading (not an error).
     */
    fun signInWithGoogle() {
        val provideIdToken = googleSignIn.provideIdToken ?: return
        viewModelScope.launch {
            _ui.value = AuthUiState(loading = true)
            val token = runCatching { provideIdToken() }.getOrNull()
            if (token == null) {
                _ui.value = AuthUiState() // cancelled or unavailable — no error banner
                return@launch
            }
            _ui.value = when (val result = auth.signInWithGoogle(token, null)) {
                is DataResult.Success -> AuthUiState() // session flips; route navigates onward
                is DataResult.Failure -> AuthUiState(error = result.error)
            }
        }
    }

    /**
     * Sign in with Apple: asks the platform for an Apple identity token + the request's raw nonce
     * (the native sheet on iOS), then exchanges it for a Firebase session. A null credential = the
     * user dismissed the sheet → silently clear loading, same as [signInWithGoogle].
     */
    fun signInWithApple() {
        val provideCredential = appleSignIn.provideCredential ?: return
        viewModelScope.launch {
            _ui.value = AuthUiState(loading = true)
            val credential = runCatching { provideCredential() }.getOrNull()
            if (credential == null) {
                _ui.value = AuthUiState() // cancelled or unavailable — no error banner
                return@launch
            }
            _ui.value = when (val result = auth.signInWithApple(credential.idToken, credential.rawNonce)) {
                is DataResult.Success -> AuthUiState() // session flips; route navigates onward
                is DataResult.Failure -> AuthUiState(error = result.error)
            }
        }
    }

    fun sendPasswordReset(email: String) {
        // Validate format locally so a malformed entry gets immediate, correct feedback instead of a
        // pointless round-trip that Firebase would reject with a misleading "invalid credentials".
        if (!Validators.isValidEmail(email)) {
            _ui.value = _ui.value.copy(error = null, resetEmailSent = false, invalidEmail = true)
            return
        }
        viewModelScope.launch {
            _ui.value = AuthUiState(loading = true)
            // AuthService treats an unknown account as success (no account-enumeration leak), so the
            // banner reads "if an account exists, we sent a reset email" regardless of existence.
            _ui.value = when (val result = auth.sendPasswordReset(email)) {
                is DataResult.Success -> AuthUiState(resetEmailSent = true)
                is DataResult.Failure -> AuthUiState(error = result.error)
            }
        }
    }

    /**
     * Gives a guest an anonymous Firebase session (uid for per-user data) without blocking their way
     * into the app: best-effort fire-and-forget — if it fails (offline, provider disabled) the guest
     * still browses, just without a uid. No-op when a session (guest or real) already exists.
     */
    fun continueAsGuest() {
        if (auth.currentUser != null) return
        viewModelScope.launch { auth.signInAnonymously() }
    }

    fun signOut() {
        viewModelScope.launch { auth.signOut() }
    }

    private val _deleteAccount = MutableStateFlow(DeleteAccountUiState())
    val deleteAccount: StateFlow<DeleteAccountUiState> = _deleteAccount.asStateFlow()

    /**
     * Permanently deletes the signed-in account (data wipe + auth user; see `AuthService`). Email
     * accounts must pass their [password] for reauthentication; a blank one short-circuits to
     * [AppError.InvalidCredentials] locally.
     */
    fun deleteAccount(password: String?) {
        if (_deleteAccount.value.working) return
        val needsPassword = session.value?.email != null
        if (needsPassword && password.isNullOrBlank()) {
            _deleteAccount.value = DeleteAccountUiState(error = AppError.InvalidCredentials())
            return
        }
        viewModelScope.launch {
            _deleteAccount.value = DeleteAccountUiState(working = true)
            _deleteAccount.value = when (val result = auth.deleteAccount(password)) {
                is DataResult.Success -> DeleteAccountUiState(done = true)
                is DataResult.Failure -> DeleteAccountUiState(error = result.error)
            }
        }
    }

    /** Clears the deletion flow state (sheet dismissed or navigation consumed `done`). */
    fun resetDeleteAccount() {
        if (!_deleteAccount.value.working) _deleteAccount.value = DeleteAccountUiState()
    }

    /** Clears a shown error / reset confirmation after the UI has consumed it (e.g. field edited). */
    fun consumeMessages() {
        _ui.value = _ui.value.copy(error = null, resetEmailSent = false, invalidEmail = false)
    }

    private fun submitCredentials(email: String, password: String, call: suspend () -> DataResult<AuthUser>) {
        if (email.isBlank() || password.isBlank()) {
            _ui.value = _ui.value.copy(error = AppError.InvalidCredentials())
            return
        }
        viewModelScope.launch {
            _ui.value = AuthUiState(loading = true)
            _ui.value = when (val result = call()) {
                is DataResult.Success -> AuthUiState() // session flips; route navigates onward
                is DataResult.Failure -> AuthUiState(error = result.error)
            }
        }
    }
}

/** Koin bindings for the auth/session layer. Aggregated by the app composition root. */
val authModule = module {
    viewModel { AuthViewModel(get(), get(), get(), get()) }
    single { UserPreferencesSync(get(), get(), get(), get(), get(), get(), get()) }
}
